import { useState, useEffect } from 'react';
import { 
  Mail, 
  Edit2, 
  Save, 
  RefreshCw, 
  Loader2,
  Eye,
  Code,
  FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { emailTemplatesApi } from '@/services/api';
import { useToast } from '@/hooks/use-toast';

export const EmailTemplatesEditor = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({ subject: '', html_content: '' });
  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState('preview');
  const { toast } = useToast();

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const data = await emailTemplatesApi.getAll();
      setTemplates(data);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      toast({ title: 'Error', description: 'Failed to load templates', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleEdit = (template) => {
    setFormData({
      subject: template.subject,
      html_content: template.html_content
    });
    setEditingTemplate(template);
    setPreviewMode('preview');
  };

  const handleSave = async () => {
    if (!editingTemplate) return;
    
    setSaving(true);
    try {
      await emailTemplatesApi.update(editingTemplate.id, formData);
      toast({ title: 'Template saved', description: `${editingTemplate.name} has been updated` });
      await fetchTemplates();
      setEditingTemplate(null);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const getPreviewHtml = () => {
    // Replace variables with example values for preview
    const exampleValues = {
      VendorContactName: 'Jane Smith',
      InviteLink: 'https://gocompareai.com/vendor/activate?token=example123',
      ExpiryTime: '7 days',
      AdminName: 'GoCompareAI Team',
      SupportEmail: 'info@gocompareai.com',
      ResetLink: 'https://gocompareai.com/vendor/reset-password?token=example456',
      LoginLink: 'https://gocompareai.com/vendor',
    };
    
    let html = formData.html_content;
    for (const [key, value] of Object.entries(exampleValues)) {
      html = html.replace(new RegExp(`{{${key}}}`, 'g'), value);
    }
    return html;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Email Templates</h2>
          <p className="text-sm text-muted-foreground">Customize vendor notification emails</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchTemplates}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Template List */}
      <div className="grid gap-4">
        {templates.map(template => (
          <Card key={template.id}>
            <CardContent className="py-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-muted rounded-lg">
                    <Mail className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div>
                    <h3 className="font-medium">{template.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Subject: {template.subject}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {template.variables?.map(v => (
                        <Badge key={v} variant="secondary" className="text-xs">
                          {`{{${v}}}`}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={() => handleEdit(template)}>
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editingTemplate} onOpenChange={() => setEditingTemplate(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Edit: {editingTemplate?.name}</DialogTitle>
            <DialogDescription>
              Customize the email template. Use variables like {`{{VendorContactName}}`} for dynamic content.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-hidden mt-4">
            <div className="space-y-4 h-full flex flex-col">
              <div className="space-y-2">
                <Label>Subject Line</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Email subject..."
                />
              </div>
              
              <div className="space-y-2">
                <Label>Available Variables</Label>
                <div className="flex flex-wrap gap-1">
                  {editingTemplate?.variables?.map(v => (
                    <Badge 
                      key={v} 
                      variant="outline" 
                      className="text-xs cursor-pointer hover:bg-primary hover:text-primary-foreground"
                      onClick={() => {
                        const textarea = document.querySelector('textarea');
                        if (textarea) {
                          const start = textarea.selectionStart;
                          const end = textarea.selectionEnd;
                          const text = formData.html_content;
                          const newText = text.substring(0, start) + `{{${v}}}` + text.substring(end);
                          setFormData(prev => ({ ...prev, html_content: newText }));
                        }
                      }}
                    >
                      {`{{${v}}}`}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <Tabs value={previewMode} onValueChange={setPreviewMode} className="flex-1 flex flex-col min-h-0">
                <TabsList>
                  <TabsTrigger value="preview" className="gap-2">
                    <Eye className="w-4 h-4" />
                    Preview
                  </TabsTrigger>
                  <TabsTrigger value="code" className="gap-2">
                    <Code className="w-4 h-4" />
                    HTML Code
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="preview" className="flex-1 min-h-0 mt-2">
                  <div className="border rounded-md h-80 overflow-auto bg-white">
                    <iframe
                      srcDoc={getPreviewHtml()}
                      className="w-full h-full"
                      title="Email Preview"
                    />
                  </div>
                </TabsContent>
                
                <TabsContent value="code" className="flex-1 min-h-0 mt-2">
                  <Textarea
                    value={formData.html_content}
                    onChange={(e) => setFormData(prev => ({ ...prev, html_content: e.target.value }))}
                    className="h-80 font-mono text-sm"
                    placeholder="HTML content..."
                  />
                </TabsContent>
              </Tabs>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setEditingTemplate(null)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
